import React, { useEffect, useState } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Menu,
    MenuItem
} from '@mui/material';
import SortIcon from '../../../assets/images/Dashboard/chevron-down.svg';
import ascToDesc from '../../../assets/images/CustomerManagement/ascToDesc.svg';
import descToAsc from '../../../assets/images/CustomerManagement/descToAsc.svg';
import SbActionDropdownButton from '../../Buttons/SbActionDropdownButton';
import StatusBadgeWithDropdown from '../../Buttons/StatusBadgeWithDropdown';
import PaginationComponent from '../../PaginationComponent';
import noDataFound from '../../../assets/images/no-data-found.svg';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

const columns = [
    { id: 'ModuleFieldID', label: 'MODULE FIELD ID', align: 'left', minWidth: '180px' },
    { id: 'Module', label: 'MODULE NAME', align: 'left', minWidth: '180px' },
    { id: 'ModuleFieldName', label: 'FIELD NAME', align: 'left', minWidth: '150px' },
    { id: 'ModuleFieldAliasName', label: 'ALIAS NAME', align: 'left', minWidth: '150px' },
    { id: 'PageName', label: 'PAGE NAME', align: 'left', minWidth: '150px' },
    { id: 'ColumnDataType', label: 'COLUMN DATA TYPE', align: 'left', minWidth: '200px' },
    { id: 'IsForeignKey', label: 'IS FOREIGNKEY', align: 'left', minWidth: '150px' },
    { id: 'ForeignKeyTableName', label: 'TABLE NAME', align: 'left', minWidth: '150px' },
    { id: 'ForeignKeyColumnName', label: 'COLUMN NAME', align: 'left', minWidth: '150px' },
    { id: 'ForeignKeyColumnFieldName', label: 'COLUMN FIELD NAME', align: 'left', minWidth: '200px' },
    { id: 'Status', label: 'STATUS', align: 'left', minWidth: '80px' },
    { id: 'action', label: 'ACTION', align: 'left', minWidth: '80px', sortable: false },
];

const ModuleFieldTable = ({ getModuleFieldData = [], loading, page, setPage, rowsPerPage, setRowsPerPage, totalDataCount, handleOpenModal }) => {
    const [order, setOrder] = useState('asc');
    const [orderBy, setOrderBy] = useState('id');
    const [anchorEl, setAnchorEl] = useState(null);
    const [currentSortColumn, setCurrentSortColumn] = useState(null);
    const [actionAnchorEl, setActionAnchorEl] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [paginatedData, setPaginatedData] = useState([]);

    const handleRequestSort = (event, property) => {
        setCurrentSortColumn(property);
        setAnchorEl(event.currentTarget);
    };

    const handleSortOrderChange = (order) => {
        setOrder(order);
        setOrderBy(currentSortColumn);
        setAnchorEl(null);
    };

    const handleActionClick = (event, customer) => {
        setSelectedCustomer(customer);
        setActionAnchorEl(event.currentTarget);
    };

    const handleActionClose = () => {
        setActionAnchorEl(false);
    };

    const handleMenuItemClick = (action) => {
        if (action === 'edit') {
            console.log('Edit Details for:', selectedCustomer);
            // Implement edit functionality here
        } else if (action === 'view') {
            console.log('View Details for:', selectedCustomer);
            // Implement view functionality here
        }
        handleActionClose();
    };

    const sortedData = React.useMemo(() => {
        const stabilizedData = getModuleFieldData.map((el, index) => [el, index]);
        stabilizedData.sort((a, b) => {
            const orderModifier = order === 'asc' ? 1 : -1;
            if (a[0][orderBy] < b[0][orderBy]) {
                return -1 * orderModifier;
            }
            if (a[0][orderBy] > b[0][orderBy]) {
                return 1 * orderModifier;
            }
            return a[1] - b[1];
        });
        return stabilizedData.map((el) => el[0]);
    }, [getModuleFieldData, order, orderBy]);

    useEffect(() => {
        if (!getModuleFieldData || getModuleFieldData.length === 0) {
            console.warn("⚠️ No Module Field Data Found!");
            setPaginatedData([]);
            return;
        }

        // Sort data based on order & orderBy
        const sorted = [...getModuleFieldData].sort((a, b) => {
            const orderModifier = order === 'asc' ? 1 : -1;
            return a[orderBy] < b[orderBy] ? -1 * orderModifier : 1 * orderModifier;
        });

        // Calculate pagination
        const maxPage = Math.ceil(sorted.length / rowsPerPage);
        const newPage = Math.min(page, maxPage - 1); // Prevents out-of-range pages

        const start = newPage * rowsPerPage;
        const end = start + rowsPerPage;
        const paginated = sorted.slice(start, end);

        setPaginatedData(paginated);
    }, [getModuleFieldData, order, orderBy, page, rowsPerPage]);

    const handlePageChange = (newPage) => {
        setPage(newPage - 1); // Handle 1-based to 0-based page index for slicing
    };
    const handleRowsPerPageChange = (newRowsPerPage) => {
        setRowsPerPage(newRowsPerPage);
        setPage(0); // Reset to the first page when rows per page changes
    };

    return (
        <>
            <TableContainer component={Paper} sx={{ maxHeight: 440 }} className='table-container'>
                <Table>
                    <TableHead>
                        <TableRow>
                            {columns.map((column) => (
                                <TableCell key={column.id} align={column.align} style={{ minWidth: column.minWidth }}>
                                    <div style={{ display: 'flex', alignItems: 'center' }} className='table-header-div'>
                                        {column.label}
                                        {column.sortable !== false && (
                                            <IconButton
                                                size="small"
                                                onClick={(event) => handleRequestSort(event, column.id)}
                                            >
                                                <img src={SortIcon} alt="Sort" />
                                            </IconButton>
                                        )}
                                    </div>
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            [...Array(rowsPerPage)].map((_, index) => (
                                <TableRow key={index}>
                                    {columns.map((column) => (
                                        <TableCell key={column.id} align={column.align}>
                                            <Skeleton height={40} />
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        ) : paginatedData.length === 0 ? (
                            // Display "No Data Found" row if no data is present in the paginated data
                            <TableRow>
                                <TableCell colSpan={columns.length} align="center">
                                    <img src={noDataFound} alt="" style={{ height: "250px", width: "280px" }} />
                                </TableCell>
                            </TableRow>
                        ) : (
                            paginatedData.map((customer) => (
                                <TableRow key={customer.id}>
                                    {columns.map((column) => (
                                        <TableCell key={column.id} align={column.align}>
                                            {column.id === 'Status' ? (
                                                // <span
                                                // className={`status-badge-for-enabling ${customer.status === 'Enable' ? 'complete-status' : 'cancel-status'}`}
                                                // >
                                                //     {customer.status}&nbsp;&nbsp;&nbsp;&nbsp;
                                                //     <span className="dropdown-arrow">
                                                //         <img src={SortIcon} alt="dropdown icon" />
                                                //     </span>
                                                // </span>
                                                <StatusBadgeWithDropdown customer={customer}
                                                    activeText="Active"
                                                    inactiveText="Inactive"
                                                />
                                            ) : column.id === 'action' ? (
                                                <SbActionDropdownButton
                                                    options={[{ type: 'Edit' }]}
                                                    openAddModal={() => { handleOpenModal(customer.ModuleFieldID) }}
                                                />
                                            ) : (
                                                customer[column.id] || "NA"
                                            )}
                                        </TableCell>

                                    ))}
                                </TableRow>
                            )))}
                    </TableBody>
                </Table>
                <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={() => setAnchorEl(null)}
                >
                    <MenuItem onClick={() => handleSortOrderChange('asc')}>
                        <img src={ascToDesc} alt="" />&nbsp;&nbsp;
                        Ascending to Descending
                    </MenuItem>
                    <MenuItem onClick={() => handleSortOrderChange('desc')}>
                        <img src={descToAsc} alt="" />&nbsp;&nbsp;
                        Descending to Ascending
                    </MenuItem>
                </Menu>
            </TableContainer>
            {/* Use PaginationComponent */}
            < PaginationComponent
                totalItems={totalDataCount}
                itemsPerPage={rowsPerPage}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
            />
        </>
    );
};

export default ModuleFieldTable;
